/**
 * assembly/input/menu.ts —— 输入管线候选菜单内核（B-7 全套 · 纯内核零 DOM）
 * **逐字移植自 102 `frontend_src/src/assembly/input/menu.js`**（含全部注释/判据/裁决序），JS→TS。
 * 依据：设计件 §7.1（光标处 / @ ⇒ 分组候选菜单／注册序裁决／Tab 语义／排序）／§7.2／§7.3.1
 *  施工方案 §八 5-A／《前端升级方案_新面补全到旧面退役_20260918》§1.5 U-1c 行 B-7：
 *  ① 内核零 DOM ② 菜单开 ⇒ composer **保持焦点** ③ 选中态走 `aria-activedescendant`
 *  ④ `matchSpace`／`matchEnter` 按**注册序**、首个非 undefined 胜出
 *  ⑤ Tab 只作用于高亮项、**无高亮原样放行** ⑥ `/` **绝不静默降级为普通提示词**
 *
 * 纪律（本文件是**纯内核**）：
 *  - **零 DOM／零时钟／零网络**；所有返回 Object.freeze（不可变）。
 *  - **无业务判定**：候选来自 source（命令源复用 `commands.ts::commandCandidates`——单一事实源）。
 *  - 菜单开 ⇒ 焦点不进菜单：内核只产出**高亮项 id**，焦点纪律由组件遵守（`aria-activedescendant`）。
 */
import { tokenAtCursor, type Token } from "./tokenize";
import { commandCandidates, type CommandMode } from "./commands";

/** 高亮项的动作（B-7：声明可展开者 ⇒ drill；普通项 ⇒ pick） */
export const MENU_ACTION = Object.freeze({ PICK: "pick", DRILL: "drill" } as const);

/** 候选项 DOM id 前缀（`aria-activedescendant` 指它） */
export const MENU_OPTION_ID_PREFIX = "composer-option-";

export interface MenuItem {
  id: string;
  kind: string;
  sigil: string;
  name: string;
  label: string;
  value: string;
  describe: string;
  input: boolean;
  mode: string;
  drillable: boolean;
}

export interface MenuState {
  open: boolean;
  sigil: string;
  prefix: string;
  token: Token | null;
  items: readonly MenuItem[];
  highlight: number;
  generation: number;
}

export interface MenuCtx { [k: string]: unknown }

export interface MenuHookResult {
  handled: true;
  action: string;
  item: MenuItem;
  content: string;
  cursor: number;
}

export interface MenuSource {
  id: string;
  sigil: string;
  candidates(prefix: string, ctx?: MenuCtx): MenuItem[];
  matchEnter?(input: { state: MenuState; text: string; cursor?: number }, ctx?: MenuCtx): MenuHookResult | undefined;
  matchSpace?(input: { state: MenuState; text: string; cursor?: number }, ctx?: MenuCtx): MenuHookResult | undefined;
}

/** 空菜单态（未打开；冻结） */
export function emptyMenuState(): MenuState {
  return Object.freeze({
    open: false, sigil: "", prefix: "", token: null, items: Object.freeze([]), highlight: -1, generation: 0,
  });
}

/** 关闭态（保留 generation 递增，便于调用方识别「换代」） */
function closedState(prev: MenuState): MenuState {
  const g = prev && typeof prev.generation === "number" ? prev.generation : 0;
  return Object.freeze({ ...emptyMenuState(), generation: g + 1 });
}

/** 候选排序（§7.1 排序行）：前缀命中在前，其次**有序子序列**模糊命中，同分保持**注册序**。
 *  完全不命中的不进候选（此规则命令源与引用源共用——A10 一套机制）。 */
export function rankCandidates(list: readonly string[], prefix: string): string[] {
  const src = Array.isArray(list) ? list : [];
  const p = String(prefix === undefined || prefix === null ? "" : prefix).toLowerCase();
  if (!p) return src.map((n) => String(n));
  const prefixHit: string[] = [];
  const fuzzyHit: string[] = [];
  for (const raw of src) {
    const name = String(raw);
    const low = name.toLowerCase();
    if (low.startsWith(p)) prefixHit.push(name);
    else if ([...p].every((ch) => low.includes(ch))) fuzzyHit.push(name); // 有序子序列（同 commands.ts 口径）
  }
  return prefixHit.concat(fuzzyHit);
}

/** 候选项工厂（字段一致 ⇒ 组件渲染与内核裁决都不需要分支） */
function makeItem(
  kind: string, sigil: string, name: string, label: string, value: string,
  extra: { describe?: string; input?: boolean; mode?: CommandMode | string; drillable?: boolean } = {},
): MenuItem {
  return Object.freeze({
    id: kind + ":" + name, kind, sigil, name, label, value,
    describe: extra.describe || "", input: !!extra.input, mode: extra.mode || "", drillable: !!extra.drillable,
  });
}

/** 已敲名字与高亮候选**完全一致** ⇒ 钩子不消费（此时 Enter 应落到「提交/执行」而非再补全一次） */
export function isExactToken(token: Token | null, item: MenuItem | null): boolean {
  if (!token || !item) return false;
  return String((token as { name?: string }).name || "") === String(item.name || "");
}

/** 选中候选 ⇒ 替换触发段（纯文本运算：值 ＋ 一个空格；光标落在其后）。 */
export function applyPick(text: string, token: { start: number; end: number } | null, value: string): { content: string; cursor: number } {
  const src = typeof text === "string" ? text : "";
  const t = token && typeof token.start === "number" && typeof token.end === "number"
    ? token
    : { start: src.length, end: src.length };
  const before = src.slice(0, t.start);
  const after = src.slice(t.end);
  const inserted = String(value === undefined || value === null ? "" : value) + " ";
  return Object.freeze({ content: before + inserted + after, cursor: before.length + inserted.length });
}

/** 补全钩子（matchSpace/matchEnter 共用）：无高亮/非本类候选/已完全一致 ⇒ 返回 undefined（不消费） */
function pickHook(kind: string) {
  return (input: { state: MenuState; text: string; cursor?: number }): MenuHookResult | undefined => {
    const s = input && input.state;
    if (!s || !s.open || s.highlight < 0) return undefined;
    const item = s.items[s.highlight];
    if (!item || item.kind !== kind) return undefined;
    if (isExactToken(s.token, item)) return undefined;
    const applied = applyPick(input.text, s.token as { start: number; end: number } | null, item.value);
    return { handled: true, action: MENU_ACTION.PICK, item, content: applied.content, cursor: applied.cursor };
  };
}

/** 命令候选源（`/`）——候选表复用 `commands.ts::commandCandidates`（单一事实源，不另造第二张表）。
 *  注册序首位（§7.1：同分保持注册序 ⇒ 命令源在引用源之前）。 */
export function createCommandSource(): MenuSource {
  return {
    id: "commands",
    sigil: "/",
    candidates(prefix: string): MenuItem[] {
      return commandCandidates(prefix).map((c) => makeItem(
        "command", "/", c.name.slice(1), c.name, c.name,
        { describe: c.describe, input: c.input, mode: c.mode },
      ));
    },
    matchEnter: pickHook("command"),
    matchSpace: pickHook("command"),
  };
}

/** `@` 引用候选源——名字表由**持有方**供给（宿主/父层）：
 *   - `names`：静态表；`getNames`：取值函数（组件用它读 props，保持响应式而不把响应式塞进内核）。
 *  🔴 本批**无引用目录通道** ⇒ 生产接线传空表（`[]`）；空表**不得**伪装成「有候选」（明记，不编）。 */
export function createRefSource({ names = [], getNames = null }: { names?: readonly string[]; getNames?: (() => unknown) | null } = {}): MenuSource {
  const read = (): readonly string[] => {
    if (typeof getNames === "function") {
      const v = getNames();
      return Array.isArray(v) ? v : [];
    }
    return Array.isArray(names) ? names : [];
  };
  return {
    id: "refs",
    sigil: "@",
    candidates(prefix: string): MenuItem[] {
      return rankCandidates(read(), prefix).map((n) => makeItem("ref", "@", n, "@" + n, "@" + n));
    },
    matchEnter: pickHook("ref"),
    matchSpace: pickHook("ref"),
  };
}

/** 候选集是否同一（同 id 序）——同一集 ⇒ 光标事件**不动高亮**（否则方向键选中的项会被重算清掉） */
function sameItems(a: readonly MenuItem[], b: readonly MenuItem[]): boolean {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].id !== b[i].id) return false;
  }
  return true;
}

/** 建立输入菜单内核。`sources` 数组序＝注册序（§7.1 裁决序）。 */
export function createInputMenu({ sources = [] }: { sources?: MenuSource[] } = {}) {
  const list = Object.freeze((Array.isArray(sources) ? sources : []).filter((s) => s && typeof s.id === "string"));
  const seen = new Set<string>();
  for (const s of list) {
    if (seen.has(s.id)) throw new TypeError("createInputMenu：source id 重复（" + s.id + "）");
    seen.add(s.id);
  }

  /** 认领 sigil 的源＝注册序里第一个（A10：一个 sigil 一个源，禁两套） */
  function sourceFor(sigil: string): MenuSource | null {
    return list.find((s) => s.sigil === sigil) || null;
  }

  function fromCursor(prev: MenuState, text: string, cursor: number | undefined, ctx: MenuCtx): MenuState {
    const token = tokenAtCursor(text, cursor);
    if (!token || token.kind === "text") return prev.open ? closedState(prev) : prev;  // 窄化：text 无 sigil
    const src = sourceFor(token.sigil as string);
    const items = src ? (src.candidates((token as { name: string }).name, ctx) || []) : [];
    if (prev.open && prev.sigil === token.sigil && prev.prefix === (token as { name: string }).name
      && prev.token && prev.token.start === token.start && sameItems(prev.items, items)) {
      return prev; // 同触发段同候选集 ⇒ 状态原地不动（高亮不被光标事件清掉）
    }
    return Object.freeze({
      open: true,
      sigil: token.sigil as string,
      prefix: (token as { name: string }).name,
      token: Object.freeze({ ...token }) as Token,
      items: Object.freeze(items),
      highlight: items.length ? 0 : -1,
      generation: (prev.generation || 0) + 1,
    });
  }

  /** 归约（纯函数，返回新状态）。事件：`cursor`／`close`／`move`／`highlight`。 */
  function reduce(state: MenuState, event: { type?: string; text?: string; cursor?: number; delta?: number; index?: number }, ctx: MenuCtx = {}): MenuState {
    const s = state && typeof state === "object" ? state : emptyMenuState();
    const type = event && event.type;
    if (type === "close") return s.open ? closedState(s) : s;
    if (type === "move") {
      if (!s.open || s.items.length === 0) return s;
      const n = s.items.length;
      const delta = Number(event.delta) || 0;
      const next = (((s.highlight + delta) % n) + n) % n;
      return Object.freeze({ ...s, highlight: next });
    }
    if (type === "highlight") {
      const i = Number(event.index);
      if (!s.open || !Number.isInteger(i) || i < 0 || i >= s.items.length) return s;
      return Object.freeze({ ...s, highlight: i });
    }
    if (type === "cursor") return fromCursor(s, String(event.text ?? ""), event.cursor, ctx);
    return s;
  }

  /** 注册序裁决（§7.1 裁决行原文）：按注册序轮询各 source 的钩子，**首个非 undefined 的应答胜出**。
   *  undefined ⇒ 无人认领（调用方按原义放行）。 */
  function resolve(hook: "matchSpace" | "matchEnter", input: { state: MenuState; text: string; cursor?: number }, ctx: MenuCtx = {}): { sourceId: string; result: MenuHookResult } | undefined {
    for (const s of list) {
      const fn = s[hook];
      if (typeof fn !== "function") continue;
      const r = fn(input, ctx);
      if (r !== undefined) return Object.freeze({ sourceId: s.id, result: r });
    }
    return undefined;
  }

  /** Tab 语义（§7.1 Tab 行）：只作用于**高亮项**；声明可展开者 ⇒ `drill`，普通项 ⇒ `pick`；
   *  **无高亮项 ⇒ 返回 undefined（原样放行，不吞 Tab，不破坏原生焦点遍历）**。 */
  function tabOutcome(state: MenuState): { action: string; item: MenuItem } | undefined {
    const s = state;
    if (!s || !s.open || s.highlight < 0 || s.items.length === 0) return undefined;
    const item = s.items[s.highlight];
    if (!item) return undefined;
    return Object.freeze({ action: item.drillable ? MENU_ACTION.DRILL : MENU_ACTION.PICK, item });
  }

  /** 选中态承载（§7.1 焦点行）：返回高亮项 DOM id；无高亮 ⇒ null（调用方据此移除该属性） */
  function activeDescendantId(state: MenuState): string | null {
    if (!state || !state.open || state.highlight < 0 || !state.items || !state.items.length) return null;
    return MENU_OPTION_ID_PREFIX + state.highlight;
  }

  /** 候选项 DOM id（与 activeDescendantId 同一命名空间） */
  function optionId(index: number): string {
    return MENU_OPTION_ID_PREFIX + index;
  }

  return { sources: list, reduce, resolve, tabOutcome, activeDescendantId, optionId, sourceFor };
}
